"use client";

import { ReactNode, useState, useEffect, useCallback, useRef, Suspense, Fragment } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useAuth } from "@/hooks/useAuth";
import { authFetch } from "@/lib/authFetch";
import { useVisibilityRef } from "@/hooks/useVisibilityRef";
import { useRedeemableQuery } from "@/hooks/useRedeemableQuery";
import { useSidebarStatsQuery } from "@/hooks/useSidebarStatsQuery";
import { sourceUrl } from "@/lib/source-url";
import { useUserMe } from "@/contexts/UserMeContext";
import CdnImage from "@/components/CdnImage";
import dynamic from "next/dynamic";
import { TrendingUp, BarChart3, MessageSquare, Users, Search, Bell, Settings, Gift, Send, Mail, ExternalLink, X, Plus, Copy, PartyPopper, Menu, BookOpen, Newspaper, Share2, MessageCircle, Home, ChevronLeft, ChevronRight, Activity, CreditCard, User as UserIcon, ShoppingBag } from "lucide-react";

import {
  ChevronLeftIcon,
  ChevronRightIcon,
  HouseIcon,
  TrendingUpDownIcon,
  ChartColumnIcon,
  ActivityIcon,
  UsersIcon,
  UserStarIcon,
  CopyIcon,
  HandCoinsIcon,
  MessageCircleIcon,
  TwitterIcon,
  SendIcon,
  MailIcon,
  BookOpenTextIcon,
  SettingsIcon,
  BellIcon,
  GavelIcon,
  UmaIcon,
  AtomIcon,
  PlusIcon,
  LinkIcon,
  WalletMinimalIcon,
} from "./app-layout/sidebar-dynamic-icons";
import WhoToFollow from "./WhoToFollow";
import MostCopied from "./MostCopied";
import TrendingMarkets from "./TrendingMarkets";
import TrendingPits from "./TrendingPits";
import ShareToEarnPromoCard from "@/app/activity/components/ShareToEarnPromoCard";
import StreakWidget from "./StreakWidget";
import { UserPill } from "@privy-io/react-auth/ui";
import AuthButton from "./AuthButton";
import SignInButton from "./SignInButton";
import MobileUserAvatar from "./MobileUserAvatar";
import { usePrivy } from "@privy-io/react-auth";
import ThemeSwitcher from "./ThemeSwitcher";
import GlobalSearch from "./GlobalSearch";
import { usePolymarketBalanceContext } from "@/contexts/PolymarketBalanceContext";
import { useSidebar } from "@/contexts/SidebarContext";
import { useTerminalMarket } from "@/contexts/TerminalMarketContext";

import { useLeadModalStore } from "@/stores/useLeadModalStore";
import { useRedeemModalStore } from "@/stores/useRedeemModalStore";
import { useDepositModalStore } from "@/stores/useDepositModalStore";
// Modals only render after a user action (button click). Lazy-load them
// so the layout shell ships ~2.5k LoC less + qrcode.react + canvas-confetti
// off the initial bundle. SSR off because they all consume browser-only
// hooks (Privy, useBodyScrollLock, Zustand stores).
const CreatePostModal = dynamic(() => import("./CreatePostModal"), { ssr: false });
const SupportChatWidget = dynamic(() => import("./SupportChatWidget"), { ssr: false });
const LevelUpWatcher = dynamic(() => import("./LevelUpWatcher"), { ssr: false });
const RedeemModal = dynamic(() => import("./RedeemModal"), { ssr: false });
const DepositModal = dynamic(() => import("./DepositModal"), { ssr: false });
// Onboarding mandatory for new users — must mount post-auth so the
// Enable-Trading modal opens immediately and wins the race against
// FrenPicker / Privy account-link modals. Previously inside <Deferred>,
// requestIdleCallback delayed it enough that other modals stole focus.
const OnboardingProvider = dynamic(() => import("./OnboardingProvider"), { ssr: false });
const PolymarketVolumeMilestoneModalGlobal = dynamic(
  () => import("./PolymarketVolumeMilestoneModalGlobal"),
  { ssr: false },
);
import {
  BscDepositModalWrapper,
  HyperliquidWithdrawModalGlobal,
  PolymarketWithdrawModalGlobal,
  HyperliquidEnableModalGlobal,
} from "./app-layout/GlobalModals";
import { SharedTradingModals } from "./app-layout/SharedTradingModals";
import { SafeUserPill } from "./app-layout/SafeUserPill";
import { MobileMenuWalletAddress } from "./app-layout/MobileMenuWalletAddress";
import { useBscDepositModalStore } from "@/stores/useBscDepositModalStore";
import NotificationsDropdown from "./NotificationsDropdown";
import SourceSelector from "./SourceSelector";
import LanguageSelector from "./LanguageSelector";
import { useT, useTCategory } from "@/hooks/useT";
import { useUserProfile } from "@/contexts/UserProfileContext";
import { useCurrentSource } from "@/hooks/useCurrentSource";
import SourceAwareBalance from "./SourceAwareBalance";
import { TelegramIcon as TgGlyph } from "./icons/TelegramIcon";
import EoaSweepBanner from "./EoaSweepBanner";
import DepositWrapBanner from "./DepositWrapBanner";
import CopytradingSkipBanner from "./CopytradingSkipBanner";
import CopyPausedBanner from "./CopyPausedBanner";
import PolymarketGeoblockBanner from "./PolymarketGeoblockBanner";
import DextoolsWelcomeBanner from "./DextoolsWelcomeBanner";
// Modal is only rendered on click; lazy-loaded so the header doesn't
// ship framer-motion + Privy hooks for a flow most users never open.
const TelegramBotModal = dynamic(
  () => import("./TelegramBotModal").then((m) => ({ default: m.TelegramBotModal })),
  { ssr: false },
);

// No props needed — layout is fully route-aware

// Helper to get user display name from Privy user object
const getUserDisplayName = (user: any): string => {
  if (!user) return "Fren";

  // Priority order for display name (only real names, not emails)
  // 1. Google name
  if (user.google?.name) return user.google.name.split(" ")[0];

  // 2. Twitter name
  if (user.twitter?.name) return user.twitter.name.split(" ")[0];

  // 3. Telegram firstName
  if (user.telegram?.firstName) return user.telegram.firstName;

  // 4. Farcaster displayName
  if (user.farcaster?.displayName) return user.farcaster.displayName.split(" ")[0];

  // 5. Discord username
  if (user.discord?.username) return user.discord.username;

  // Fallback (includes email-only users)
  return "Fren";
};

// Get the Markets href based on auth + saved source
// NOTE: isMounted must be true only after useEffect to avoid SSR hydration mismatch
function getMarketsHref(isAuthenticated: boolean, isMounted: boolean): string {
  if (!isAuthenticated) return "/polymarket/markets";
  if (!isMounted) return "/polymarket/markets/for-you";
  const saved = localStorage.getItem("frenflow_source");
  // Hyperliquid lives at /hyperliquid/markets (no for-you / trending split
  // yet), so route directly. Without this branch the saved=hyperliquid
  // user would silently get bumped back into Polymarket every time they
  // tap Markets in the sidebar.
  if (saved === "hyperliquid") return "/hyperliquid/markets";
  if (saved && (saved === "kalshi" || saved === "polymarket")) {
    return sourceUrl("/markets/trending", saved as "kalshi" | "polymarket");
  }
  if (saved === "predict") {
    return `/predictfun/markets/trending`;
  }
  return "/polymarket/markets/for-you";
}

// Get the Traders href based on saved source
function getTradersHref(isMounted: boolean): string {
  if (!isMounted) return "/traders";
  const saved = localStorage.getItem("frenflow_source");
  // Kalshi traders surface was removed (no copytrading on Kalshi) — a saved
  // Kalshi source falls through to the Polymarket leaderboard.
  if (saved === "predict") return "/traders/predict";
  return "/traders";
}

// Navigation items - Markets href is dynamic based on auth state + saved source
//
// `prefetch: false` opts the link out of Next.js viewport prefetching. The
// default (omitted) is true, which fires a `?_rsc=…` GET as soon as the
// link enters the viewport so the click is instant. Chrome DevTools traced
// 18+ of those firing during a cold /markets load — useful for the high-
// traffic destinations (Markets/Activity/Traders/Copy), wasteful for the
// rarely-visited ones (Degenbox/BubbleFlow/UMA/Affiliate/Feedback). The
// rare ones still navigate, just without the warm RSC payload — typically
// 200-500 ms slower on first click, never afterwards.
const getNavigation = (isAuthenticated: boolean, hydrated: boolean, source: string = "polymarket") => [
  // Home only shown for non-authenticated users
  ...(!isAuthenticated ? [{ name: "Home", href: "/", icon: HouseIcon, isAnimatedIcon: true, prefetch: false }] : []),
  // ─────────────────────────────────────────────────────────────────────
  // CORE (top): Markets → Traders → Copy → Portfolio → Activity.
  // 2026-06-09 reorg: the 4-5 high-traffic destinations sit up top; the
  // niche surfaces drop below a "More" separator (rendered before the first
  // More item, "Degenbox", in the nav maps). Copy is CORE — it's the
  // product's core loop (copy-trade), per product call.
  // Traders: Polymarket + Predict both expose a leaderboard surface
  // (/traders, /traders/predict synced from Predict's GraphQL) — shown for
  // both. Copy is Polymarket-only (no copytrading on Predict/Kalshi/HL).
  // Both hidden for sources without the surface (Kalshi/HL). The mobile
  // BOTTOM bar (getMobileNavigation) keeps them: fixed 4-slot layout.
  // Portfolio + Feedback are auth-gated.
  // prefetch:false on the rare ones opts them out of viewport RSC prefetch.
  // ─────────────────────────────────────────────────────────────────────
  { name: "Markets", href: getMarketsHref(isAuthenticated, hydrated), icon: TrendingUpDownIcon, isAnimatedIcon: true },
  ...(source === "polymarket" || source === "predict"
    ? [{ name: "Traders", href: getTradersHref(hydrated), icon: UsersIcon, isAnimatedIcon: true, prefetch: false }]
    : []),
  ...(source === "polymarket" || source === "predict"
    ? [{ name: "Copy", href: "/copytrading", icon: CopyIcon, isAnimatedIcon: true, prefetch: false }]
    : []),
  // authOnly: rendered ALWAYS (no isAuthenticated gate) so SSR/first-paint
  // DOM matches post-hydration — the row's visibility is driven purely by
  // the `.nav-auth-only` CSS + the pre-paint `data-auth-optimistic` attr
  // (layout.tsx inline script). Gating by isAuthenticated here made Privy's
  // async resolve POP the row in → pushed every item below it down on
  // refresh (the sidebar "jump"). 2026-06-09.
  { name: "Portfolio", href: "/portfolio", icon: WalletMinimalIcon, isAnimatedIcon: true, prefetch: false, authOnly: true },
  { name: "Activity", href: "/activity", icon: ActivityIcon, isAnimatedIcon: true },
  // ── MORE (secondary / niche) — divider injected before "Degenbox" ──
  { name: "Degenbox", href: "/degenbox", icon: ChartColumnIcon, isAnimatedIcon: true, prefetch: false },
  // BubbleFlow + UMA are Polymarket-only surfaces (UMA oracle / PM bubble
  // viz) — hidden for non-Polymarket sources.
  ...(source === "polymarket"
    ? [
        { name: "BubbleFlow", href: "/bubbleflow", icon: AtomIcon, isAnimatedIcon: true, prefetch: false },
        { name: "UMA", href: "/uma", icon: UmaIcon, isAnimatedIcon: true, prefetch: false },
      ]
    : []),
  { name: "Affiliate", href: "/affiliate", icon: HandCoinsIcon, isAnimatedIcon: true, prefetch: false },
  { name: "Feedback", href: "/feedback", icon: MessageCircleIcon, isAnimatedIcon: true, prefetch: false, authOnly: true },
];

// Mobile bottom nav - 4 items + center floating button.
// 2026-05-22: Portfolio added for authenticated users (replaces the
// less-frequented Activity slot — Activity is still reachable via the
// sidebar drawer). Same rationale as the sidebar item: users couldn't
// find /portfolio without knowing the header balance pill was
// clickable. Non-authenticated users see Activity instead since they
// have nothing to render at /portfolio.
const getMobileNavigation = (isAuthenticated: boolean, hydrated: boolean) => [
  { name: "Markets", href: getMarketsHref(isAuthenticated, hydrated), icon: TrendingUp },
  { name: "Traders", href: getTradersHref(hydrated), icon: Users },
  { name: "Copy", href: "/copytrading", icon: Copy },
  isAuthenticated
    ? { name: "Portfolio", href: "/portfolio", icon: ShoppingBag }
    : { name: "Activity", href: "/activity", icon: MessageSquare },
];

export default function AppLayoutWithSidebar({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();

  // /embed/* pages are rendered inside third-party iframes (Discord
  // Activity, dextools, etc). They MUST NOT inherit the app's sidebar
  // / header / right rail — the iframe host expects bare content,
  // sized to its own chrome. Short-circuit the rest of the layout.
  // Additional iframe-aware gating happens in <IframeGate> above us;
  // keeping this path check as a defensive redundancy.
  // /shutdown is the standalone shutdown notice — no app chrome either.
  if (pathname?.startsWith('/embed') || pathname === '/shutdown') {
    return <>{children}</>;
  }

  // Right sidebar: OFF by default, ON only for specific pages
  const showRightSidebar = !!(
    pathname?.startsWith('/activity') ||
    pathname?.startsWith('/post/')
  );
  const router = useRouter();
  const { isLoggedIn, isLoading: authLoading, isReady: privyReady } = useAuth();
  const { user, login, logout } = usePrivy();
  const { balance, portfolioValue, loading: loadingBalance, walletAddress } = usePolymarketBalanceContext();
  const { isCollapsed, setIsCollapsed, isHydrated } = useSidebar();
  const { marketData: terminalMarket } = useTerminalMarket();

  // Sidebar source awareness. "Traders" shows for Polymarket + Predict (both
  // have a leaderboard surface); "Copy", "BubbleFlow" and "UMA" are
  // Polymarket-only (no copytrading on others; UMA/Bubble are PM features).
  // These are hidden from the sidebar when the active source doesn't expose
  // them (e.g. all hidden for Kalshi/Hyperliquid). Defaults to 'polymarket' so SSR + the
  // first client render keep the items (no hydration mismatch); the effect
  // resolves the real source post-mount, accepting a one-time nav reflow for
  // non-Polymarket users. We avoid `useCurrentSource` here on purpose — it
  // reads `useSearchParams`, which would force every page through this layout
  // to render dynamically (see note at the bottom of this file).
  const [navSource, setNavSource] = useState<string>("polymarket");
  useEffect(() => {
    const resolve = () => {
      // Path-prefixed routes are authoritative.
      if (pathname.startsWith("/hyperliquid")) return setNavSource("hyperliquid");
      if (/^\/kalshi(\/|$)/.test(pathname) || pathname.startsWith("/event/kalshi/")) return setNavSource("kalshi");
      if (pathname.startsWith("/predictfun") || pathname.startsWith("/event/predict/") || pathname.startsWith("/traders/predict")) return setNavSource("predict");
      // /copytrading has no per-source URL variant, so it follows the saved
      // source preference — but only Polymarket + Predict expose copytrading.
      // A stale Kalshi/HL preference must NOT hide the Copy item the page
      // points to, so anything other than predict falls back to polymarket.
      // This also keeps BubbleFlow/UMA (PM-only) hidden when the user is in
      // Predict and lands on /copytrading.
      if (pathname.startsWith("/copytrading")) {
        const saved = typeof window !== "undefined" ? localStorage.getItem("frenflow_source") : null;
        return setNavSource(saved === "predict" ? "predict" : "polymarket");
      }
      // Home + unprefixed market/event pages are Polymarket. /traders is a
      // Polymarket-only surface (its /predict variant is handled above), so it
      // resolves to polymarket regardless of saved source — otherwise a stale
      // Kalshi preference would hide the very nav items the page points to.
      if (
        pathname === "/" ||
        /^\/(polymarket|markets|market|event)(\/|$)/.test(pathname) ||
        pathname.startsWith("/traders")
      ) return setNavSource("polymarket");
      // Other pages (settings, portfolio…) follow the saved preference.
      const saved = typeof window !== "undefined" ? localStorage.getItem("frenflow_source") : null;
      return setNavSource(saved || "polymarket");
    };
    resolve();
    const onSourceChange = (e: Event) => {
      const s = (e as CustomEvent).detail?.source;
      if (s) setNavSource(s);
    };
    window.addEventListener("frenflow:source-change", onSourceChange);
    return () => window.removeEventListener("frenflow:source-change", onSourceChange);
  }, [pathname]);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCreatePostOpen, setIsCreatePostOpen] = useState(false);
  // Mobile-only Telegram bot modal — opened from the drawer item below
  // the Settings link. Uses the shared modal component so desktop
  // (LiveTicker footer pill) and mobile share one source of truth.
  const [tgBotModalOpen, setTgBotModalOpen] = useState(false);
  const { open: openDepositModal } = useDepositModalStore();
  const [currentMarketPreview, setCurrentMarketPreview] = useState<any>(null);
  const [loadingMarketPreview, setLoadingMarketPreview] = useState(false);
  const { open: openWaitlist } = useLeadModalStore();
  const { open: openRedeemModal } = useRedeemModalStore();
  const { data: _redeemableQueryData } = useRedeemableQuery();
  const redeemableCount = _redeemableQueryData?.count ?? 0;
  const redeemableValue = _redeemableQueryData?.totalRedeemable ?? 0;
  const { data: sidebarStats } = useSidebarStatsQuery();
  const [newActivityCount, setNewActivityCount] = useState(0);
  const [unreadFeedbackReplies, setUnreadFeedbackReplies] = useState(0);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const activeReferrals = sidebarStats?.activeReferrals ?? 0;
  const copytradingCount = sidebarStats?.copytradingCount ?? 0;
  const [showTickerOverride, setShowTickerOverride] = useState<boolean | null>(null);
  const { user: userMe, loading: userMeLoading } = useUserMe();
  const t = useT();
  const tCategory = useTCategory();
  // FF profile context — exposes the user's FrenFlow username so the
  // mobile menu can route directly to /profile/<username>. Server-side
  // safe because the context falls back to null pre-hydration.
  const { profile: ffProfile } = useUserProfile();
  const ffUsername: string | null = ffProfile?.user?.username || null;
  // Sidebar nav `item.name` is the canonical EN label. We map it to the
  // catalog key by lowercasing — the keys in `messages.ts` are kept in
  // sync (`nav.markets`, `nav.activity`, …). Falls back to the EN name
  // when there's no translation, so unknown items keep working.
  const tNav = (name: string) => t(`nav.${name.toLowerCase()}`);
  const tPage = (title: string) => {
    const key = `page.${title.toLowerCase().replace(/\s+/g, "")}`;
    const translated = t(key);
    return translated === key ? title : translated;
  };
  // Optimistic default: assume the ticker is visible while userMe loads so logged-in users
  // don't see a show → hide → show flicker between Privy and userMe resolving.
  // Must stay in sync with LiveTicker.tsx so the reserved `mb-14` matches the ticker's visibility.
  const showTicker = showTickerOverride ?? (userMe?.show_ticker ?? true);
  const [isActivityView, setIsActivityView] = useState(pathname === "/activity");

  const visRef = useVisibilityRef();

  // Mobile header + bottom nav scroll behavior
  // lastScrollY es un REF, no state: se actualiza en CADA scroll significativo,
  // y como state forzaba un re-render de TODO el layout global por scroll +
  // re-suscribía el listener en cada cambio (dep del effect). Como ref, el
  // handler lee/escribe sin re-render. showMobileNav/Header siguen como state
  // (solo cambian al invertir la dirección; React deduplica el mismo valor).
  const lastScrollYRef = useRef(0);
  const [showMobileNav, setShowMobileNav] = useState(true);
  const [showMobileHeader, setShowMobileHeader] = useState(true);

  // Track hovered nav item for animated icons
  const [hoveredNavItem, setHoveredNavItem] = useState<string | null>(null);
  const [twitterHovered, setTwitterHovered] = useState(false);
  const [telegramHovered, setTelegramHovered] = useState(false);
  const [emailHovered, setEmailHovered] = useState(false);
  const [blogHovered, setBlogHovered] = useState(false);
  const [settingsHovered, setSettingsHovered] = useState(false);
  const [addFundsHovered, setAddFundsHovered] = useState(false);


  // Sync auth-optimistic attribute with actual Privy state
  // This removes the attribute when Privy confirms user is NOT logged in,
  // preventing the flash when the optimistic state was wrong
  useEffect(() => {
    if (!privyReady) return;

    if (isLoggedIn) {
      // User is confirmed logged in - keep or set the optimistic attribute
      document.documentElement.setAttribute('data-auth-optimistic', 'true');
      localStorage.setItem('frenflow_auth_optimistic', 'true');
    } else {
      // User is confirmed NOT logged in - remove the optimistic attribute
      document.documentElement.removeAttribute('data-auth-optimistic');
      localStorage.removeItem('frenflow_auth_optimistic');
    }
  }, [privyReady, isLoggedIn]);

  useEffect(() => {
    if (pathname === "/activity") {
      setIsActivityView(true);
      return;
    }

    if (pathname !== "/portfolio") {
      setIsActivityView(false);
      return;
    }

    if (typeof window === "undefined") {
      setIsActivityView(false);
      return;
    }

    const syncActivityView = () => {
      const params = new URLSearchParams(window.location.search);
      setIsActivityView(params.get("tab") === "activity");
    };

    syncActivityView();
    window.addEventListener("popstate", syncActivityView);
    return () => window.removeEventListener("popstate", syncActivityView);
  }, [pathname]);

  // Keyboard shortcuts for navigation (Cmd/Ctrl + 1-7)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only trigger with Cmd (Mac) or Ctrl (Windows)
      if (!e.metaKey && !e.ctrlKey) return;

      // Don't interfere with Cmd+C, Cmd+V, etc.
      if (e.altKey || e.shiftKey) return;

      // Mirrors the sidebar order (2026-06-09 reorg) for the logged-in case
      // so ⌘N lands on the item showing ⌘N on hover. Core 1-5, More 6-9.
      const keyMap: Record<string, string> = {
        '1': getMarketsHref(isLoggedIn, isHydrated), // Markets
        '2': '/traders',     // Traders
        '3': '/copytrading', // Copy
        '4': '/portfolio',   // Portfolio
        '5': '/activity',    // Activity
        '6': '/degenbox',    // Degenbox
        '7': '/bubbleflow',  // BubbleFlow
        '8': '/uma',         // UMA
        '9': '/affiliate',   // Affiliate
      };

      const path = keyMap[e.key];
      if (path) {
        e.preventDefault();
        router.push(path);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [router, isLoggedIn]);

  const isTerminalPage = pathname?.startsWith("/terminal/");
  const isBubbleflow = pathname?.startsWith("/bubbleflow");
  const isEventPage = pathname?.startsWith("/event/") || pathname?.startsWith("/polymarket/event/") || pathname?.startsWith("/kalshi/event/");
  const isMarketPage = pathname?.startsWith("/market/") || pathname?.startsWith("/polymarket/market/") || pathname?.startsWith("/kalshi/market/");
  const isPolyLinksPage = pathname === "/frenlinks";
  const isOGPassPage = pathname === "/og-pass";
  // Only the bespoke /hyperliquid marketing page owns its own canvas
  // (full-bleed hero + sections). Everything under /hyperliquid/* — markets
  // list, event detail, market detail — must get the same default
  // px-4 lg:px-6 chrome as the polymarket / kalshi equivalents, otherwise
  // event pages render flush against the sidebar with no horizontal gutter.
  const isHyperliquidLanding = pathname === "/hyperliquid";
  // Home page bleeds edge-to-edge (HomeV3 tickers + section backgrounds
  // own their own padding). Without this the layout wraps the hero with
  // px-4 lg:px-6 which reintroduces the "encapsulated card" feel.
  const isHomePage = pathname === "/";

  // Collapse sidebar by default on pages that need more space
  useEffect(() => {
    if (isPolyLinksPage || isOGPassPage || isBubbleflow) {
      setIsCollapsed(true);
    }
  }, [isPolyLinksPage, isOGPassPage, isBubbleflow, setIsCollapsed]);

  // Hide mobile header + nav on scroll down, show on scroll up
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const diff = currentScrollY - lastScrollYRef.current;

      // Only trigger if scroll difference is significant (avoid micro-scrolls)
      if (Math.abs(diff) > 10) {
        const show = diff < 0 || currentScrollY < 10; // Show when scrolling up or at top
        setShowMobileNav(show);
        setShowMobileHeader(show);
        lastScrollYRef.current = currentScrollY;
      }

      // Sync header visibility + scroll state to <html> for CSS-driven subcategory collapse
      if (currentScrollY > 50) {
        document.documentElement.setAttribute('data-has-scrolled', '');
      } else if (currentScrollY < 10) {
        document.documentElement.removeAttribute('data-has-scrolled');
      }
      // Mirror mobile header state to <html> (same node as data-has-scrolled)
      if (Math.abs(diff) > 10) {
        if (diff < 0 || currentScrollY < 10) {
          document.documentElement.removeAttribute('data-mobile-header-hidden');
        } else {
          document.documentElement.setAttribute('data-mobile-header-hidden', '');
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
    // Sin deps: el listener se suscribe UNA vez. lastScrollYRef es un ref, así
    // que el handler lee siempre el valor actual sin re-suscribir ni re-render.
  }, []);

  // Clear badge when visiting Activity page
  useEffect(() => {
    if (isActivityView) {
      setNewActivityCount(0);
      localStorage.setItem("frenflow_last_activity_visit", Date.now().toString());
    }
  }, [isActivityView]);

  // Sync counts from shared query (with pathname-based overrides)
  useEffect(() => {
    if (!sidebarStats) return;
    setUnreadNotifications(sidebarStats.notifications ?? 0);
    setUnreadFeedbackReplies(sidebarStats.feedbackReplies ?? 0);
    if (!isActivityView) {
      setNewActivityCount(sidebarStats.newActivityCount ?? 0);
    }
  }, [sidebarStats, isActivityView]);

  // Clear feedback notification when navigating to /feedback
  useEffect(() => {
    if (pathname === "/feedback" && unreadFeedbackReplies > 0) {
      setUnreadFeedbackReplies(0);
      authFetch("/api/feedback/mark-read", { method: "POST" }).catch(() => {});
    }
  }, [pathname, unreadFeedbackReplies]);

  // Listen for settings changes (local override for ticker visibility)
  useEffect(() => {
    const handleTickerVisibilityChange = (e: CustomEvent<boolean>) => {
      setShowTickerOverride(e.detail);
    };

    window.addEventListener('tickerVisibilityChange', handleTickerVisibilityChange as EventListener);
    return () => {
      window.removeEventListener('tickerVisibilityChange', handleTickerVisibilityChange as EventListener);
    };
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  // Extract slug from current path (handles prefixed routes)
  const getSlugFromPath = useCallback(() => {
    if (isEventPage) {
      if (pathname?.startsWith("/polymarket/event/")) return pathname.replace("/polymarket/event/", "") || null;
      if (pathname?.startsWith("/kalshi/event/")) return pathname.replace("/kalshi/event/", "").split("/")[0] || null;
      return pathname?.replace("/event/", "") || null;
    }
    if (isMarketPage) {
      if (pathname?.startsWith("/polymarket/market/")) return pathname.replace("/polymarket/market/", "") || null;
      if (pathname?.startsWith("/kalshi/market/")) return pathname.replace("/kalshi/market/", "") || null;
      return pathname?.replace("/market/", "") || null;
    }
    return null;
  }, [pathname, isEventPage, isMarketPage]);

  // Fetch market preview when on event/market page
  const fetchMarketPreview = useCallback(async () => {
    const slug = getSlugFromPath();
    if (!slug) return null;

    // Skip Polymarket preview for Kalshi/Predict pages (path prefix or ?source= param)
    if (pathname?.startsWith('/kalshi/') || pathname?.startsWith('/predict/')) return null;
    if (slug.startsWith('kalshi/') || slug.startsWith('predict/')) return null;
    if (typeof window !== 'undefined') {
      const urlSource = new URLSearchParams(window.location.search).get('source');
      if (urlSource === 'kalshi' || urlSource === 'predict') return null;
    }

    setLoadingMarketPreview(true);
    try {
      // Build Polymarket URL from slug
      const polymarketUrl = isEventPage
        ? `https://polymarket.com/event/${slug}`
        : `https://polymarket.com/market/${slug}`;

      const response = await fetch(`/api/polymarket/event?url=${encodeURIComponent(polymarketUrl)}`);
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          return data.data;
        }
      }
    } catch (error) {
    } finally {
      setLoadingMarketPreview(false);
    }
    return null;
  }, [getSlugFromPath, isEventPage, pathname]);

  // Prefetch market preview when on event/market page
  useEffect(() => {
    if (isEventPage || isMarketPage) {
      fetchMarketPreview().then(preview => {
        if (preview) {
          setCurrentMarketPreview(preview);
        }
      });
    } else {
      setCurrentMarketPreview(null);
    }
  }, [pathname, isEventPage, isMarketPage, fetchMarketPreview]);

  // Handle create post button click
  const handleCreatePostClick = useCallback(() => {
    setIsCreatePostOpen(true);
  }, []);

  const formatVolume = (vol: number) => {
    if (vol >= 1_000_000) return `$${(vol / 1_000_000).toFixed(1)}M`;
    if (vol >= 1_000) return `$${(vol / 1_000).toFixed(0)}K`;
    return `$${vol.toFixed(0)}`;
  };

  // Get user display name for greeting
  const displayName = getUserDisplayName(user);

  // Get current page title for mobile header
  const getPageTitle = () => {
    if (pathname === "/" || pathname === "/home-v2") return "";
    if (pathname === "/markets" || pathname === "/polymarket/markets" || pathname === "/kalshi/markets" || pathname === "/predictfun/markets") return "Markets";
    if (pathname?.startsWith("/markets/") || pathname?.startsWith("/polymarket/markets/") || pathname?.startsWith("/kalshi/markets/") || pathname?.startsWith("/predictfun/markets/")) {
      // Extract category from path like /markets/crypto or /polymarket/markets/crypto
      const segments = pathname.split("/");
      const category = pathname?.startsWith("/polymarket/") || pathname?.startsWith("/kalshi/") || pathname?.startsWith("/predictfun/") ? segments[3] : segments[2];
      if (category) {
        const categoryLabels: Record<string, string> = {
          'for-you': 'For You',
          'fed-rates': 'Fed Rates',
          'weather-science': 'Weather & Science',
          'climate-science': 'Weather & Science',
        };
        return categoryLabels[category] || category.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      }
      return "Markets";
    }
    if (pathname === "/predictor") return "Predictor";
    if (pathname === "/degenbox") return "Degenbox";
    if (pathname?.startsWith("/bubbleflow")) return "BubbleFlow";
    if (pathname === "/activity") return "Activity";
    if (pathname === "/traders") return "Traders";
    if (pathname === "/copytrading") return "Copy";
    if (pathname === "/affiliate" || pathname === "/affiliate/toolkit") return "Affiliate";
    if (pathname === "/feedback") return "Feedback";
    if (pathname === "/rewards") return "Rewards";
    if (pathname === "/portfolio") return "Portfolio";
    if (pathname?.startsWith("/elon")) return "Elon Tracker";
    if (pathname?.startsWith("/event/") || pathname?.startsWith("/polymarket/event/") || pathname?.startsWith("/kalshi/event/") || pathname?.startsWith("/predictfun/event/")) return "Event";
    if (pathname?.startsWith("/market/") || pathname?.startsWith("/polymarket/market/") || pathname?.startsWith("/kalshi/market/") || pathname?.startsWith("/predictfun/market/")) return "Market";
    if (pathname?.startsWith("/profile/")) return "Profile";
    if (pathname?.startsWith("/traders/")) return "Trader";
    if (pathname?.startsWith("/post/")) return "Post";
    if (pathname?.startsWith("/terminal/")) return "Terminal";
    if (pathname === "/frenlinks") return "FrenLinks";
    if (pathname?.startsWith("/claim/")) return "Claim";
    if (pathname === "/og-pass") return "OG Pass";
    if (pathname === "/settings") return "Settings";
    if (pathname === "/blog") return "Prediction Markets Blog";
    if (pathname?.startsWith("/blog/")) return "Prediction Markets Blog";
    if (pathname?.startsWith("/geopolitics")) return "Geopolitics";
    if (pathname?.startsWith("/uma")) return "UMA";
    if (pathname?.startsWith("/apuestas-la-velada-6")) return "La Velada del Año 6";
    if (pathname?.startsWith("/prediction-markets-open-interest")) return "Open Interest";
    return "";
  };

  return (
    <div className="min-h-[100dvh] bg-gray-50 dark:bg-[#0f0f12] overflow-x-clip w-full" data-main-scroll {...(!showMobileHeader ? { 'data-mobile-header-hidden': '' } : {})}>
      {/* ==================== MOBILE HEADER — iOS Glass, auto-hide on scroll ==================== */}
      {/* Fondo OPACO (sin backdrop-blur): el blur sobre una barra fixed obliga al
          compositor a re-difuminar el fondo en cada frame de scroll → jank en
          móvil. Opaco = scroll fluido. (incident perf móvil 2026-06-03) */}
      <header className={`lg:hidden fixed top-0 left-0 right-0 z-50 bg-white/95 dark:bg-[#18181D]/95 border-b border-white/20 dark:border-white/[0.08] shadow-[0_1px_3px_rgba(0,0,0,0.05)] dark:shadow-[0_1px_3px_rgba(0,0,0,0.3)] transition-transform duration-300 ${showMobileHeader ? 'translate-y-0' : '-translate-y-full'}`}>
        <div className="px-4 h-12 flex items-center gap-3">
          {authLoading ? (
            <>
              {/* Skeleton header while auth loads — prevents flash */}
              <div className="w-7 h-7 rounded-[5px] bg-gray-200 dark:bg-white/[0.08] animate-pulse" />
              <div className="flex-1 flex justify-center">
                <div className="h-4 w-24 rounded bg-gray-200 dark:bg-white/[0.08] animate-pulse" />
              </div>
              <div className="flex gap-1.5">
                <div className="w-8 h-8 rounded-[5px] bg-gray-200 dark:bg-white/[0.08] animate-pulse" />
                <div className="w-8 h-8 rounded-[5px] bg-gray-200 dark:bg-white/[0.08] animate-pulse" />
              </div>
            </>
          ) : isLoggedIn ? (
            <>
              {/* Left: Balance — fills remaining space (avatar removed 2026-06-07;
                  the notification bell moved to the right cluster to match desktop) */}
              <div className="flex-1 flex items-center justify-start min-w-0">
                <SourceAwareBalance />
              </div>
              {/* Right: Bell (same spot as desktop) + Source + Search + Menu */}
              <div className="flex items-center gap-1.5">
                <NotificationsDropdown
                  unreadCount={unreadNotifications}
                  onCountChange={setUnreadNotifications}
                />
                <SourceSelector iconOnly />
                <button
                  onClick={() => setIsSearchOpen(true)}
                  className="w-8 h-8 flex items-center justify-center rounded-[5px] bg-black/[0.04] dark:bg-white/[0.08] backdrop-blur-sm text-gray-600 dark:text-gray-300 active:scale-90 transition-transform border border-black/[0.04] dark:border-[#1A1A1A]"
                  aria-label={t("action.search")}
                >
                  <Search className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setIsMobileMenuOpen(true)}
                  className="w-8 h-8 flex items-center justify-center rounded-[5px] bg-black/[0.04] dark:bg-white/[0.08] backdrop-blur-sm text-gray-600 dark:text-gray-300 active:scale-90 transition-transform border border-black/[0.04] dark:border-[#1A1A1A]"
                  aria-label={t("action.menu")}
                >
                  <Menu className="w-4 h-4" />
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Not logged in: Logo + FREN/FLOW wordmark | Search + Sign In + Menu */}
              <Link href="/" prefetch={false} className="flex items-center gap-2 flex-shrink-0 group">
                <CdnImage
                  src="/brand/frenflow_logo_light.png"
                  alt="FrenFlow"
                  width={22}
                  height={26}
                  className="w-[22px] h-[26px] object-contain dark:hidden"
                />
                <CdnImage
                  src="/brand/frenflow_logo.png"
                  alt="FrenFlow"
                  width={22}
                  height={26}
                  className="w-[22px] h-[26px] object-contain hidden dark:block"
                />
                <div className="flex items-baseline gap-0">
                  <span className="text-lg font-monument font-bold tracking-tight text-gray-900 dark:text-white whitespace-nowrap">
                    FREN
                  </span>
                  <span className="text-lg font-satoshi font-light tracking-normal text-gray-900 dark:text-white whitespace-nowrap">
                    FLOW
                  </span>
                </div>
              </Link>
              <div className="flex-1" />
              <div className="flex items-center gap-1.5">
                <SourceSelector iconOnly />
                <button
                  onClick={() => setIsSearchOpen(true)}
                  className="w-8 h-8 flex items-center justify-center rounded-[5px] bg-black/[0.04] dark:bg-white/[0.08] backdrop-blur-sm text-gray-600 dark:text-gray-300 active:scale-90 transition-transform border border-black/[0.04] dark:border-[#1A1A1A]"
                  aria-label={t("action.search")}
                >
                  <Search className="w-4 h-4" />
                </button>
                <SignInButton onClick={() => login()} compact />
                <button
                  onClick={() => setIsMobileMenuOpen(true)}
                  className="w-8 h-8 flex items-center justify-center rounded-[5px] bg-black/[0.04] dark:bg-white/[0.08] backdrop-blur-sm text-gray-600 dark:text-gray-300 active:scale-90 transition-transform border border-black/[0.04] dark:border-[#1A1A1A]"
                  aria-label={t("action.menu")}
                >
                  <Menu className="w-4 h-4" />
                </button>
              </div>
            </>
          )}
          {/* UserPill hidden for Privy modal functionality */}
          <div className="sr-only" data-privy-user-pill aria-hidden="true">
            <UserPill />
          </div>
        </div>
      </header>

      {/* Mobile Search Modal */}
      {isSearchOpen && (
          <div
            className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm lg:hidden"
            onClick={() => setIsSearchOpen(false)}
          >
            <div
              className="bg-white dark:bg-[#18181D] m-4 p-4 rounded-[5px] shadow-2xl border border-gray-200 dark:border-[#1A1A1A]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-monument font-bold text-gray-900 dark:text-white">{t("action.search")}</h3>
                <button
                  onClick={() => setIsSearchOpen(false)}
                  className="p-2 rounded-[5px] hover:bg-gray-100 dark:hover:bg-[#0a0a0b] transition-colors"
                  aria-label={t("action.closeSearch")}
                >
                  <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                </button>
              </div>
              <GlobalSearch />
            </div>
          </div>
        )}

      {/* Mobile Menu — iOS-style glass slide-over panel */}
      {isMobileMenuOpen && (
          <div className="fixed inset-0 z-[100] lg:hidden" onClick={() => setIsMobileMenuOpen(false)} onWheel={(e) => e.preventDefault()}>
            {/* Scrim */}
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />

            {/* Glass Panel — slides from right */}
            <div
              className="absolute top-0 right-0 bottom-0 w-[85%] max-w-[360px] backdrop-blur-[40px] backdrop-saturate-[1.8] bg-white/80 dark:bg-[#18181D]/75 border-l border-white/20 dark:border-white/[0.08] shadow-[-8px_0_30px_rgba(0,0,0,0.15)] dark:shadow-[-8px_0_30px_rgba(0,0,0,0.5)] overflow-y-auto overscroll-contain"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <div className="flex items-center justify-between px-5 pt-[max(1rem,env(safe-area-inset-top))] pb-3">
                <span className="text-[13px] font-semibold text-gray-500 dark:text-gray-400">{t("menu.title")}</span>
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-[5px] bg-black/[0.05] dark:bg-white/[0.08]"
                  aria-label={t("menu.close")}
                >
                  <X size={16} className="text-gray-600 dark:text-gray-300" />
                </button>
              </div>

              {/* Profile + Wallet card — glass tile */}
              {isLoggedIn && (
                <div className="mx-4 mb-4 p-4 rounded-[5px] bg-black/[0.03] dark:bg-white/[0.04] border border-black/[0.04] dark:border-[#1A1A1A]">
                  <div className="flex items-center gap-3 mb-3">
                    <MobileUserAvatar />
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-semibold text-gray-900 dark:text-white truncate">
                        {getUserDisplayName(user)}
                      </p>
                      {/* Wallet under the user name. `useCurrentSource`
                          relies on `useSearchParams`, which forces the
                          page to bail out of static prerendering unless
                          isolated under <Suspense>. Keeping it scoped
                          here lets every other page stay statically
                          renderable. */}
                      <Suspense fallback={null}>
                        <MobileMenuWalletAddress profile={ffProfile} />
                      </Suspense>
                    </div>
                    {/* Add Funds — "+" compacto a la derecha del nombre
                        (source-aware: BSC para predict, deposit modal resto). */}
                    <button
                      onClick={() => {
                        setIsMobileMenuOpen(false);
                        const currentSource = typeof window !== 'undefined' ? localStorage.getItem('frenflow_source') : null;
                        const urlHasPredict = typeof window !== 'undefined' && (window.location.pathname.startsWith('/predictfun') || window.location.search.includes('_source=predict'));
                        if (currentSource === 'predict' || urlHasPredict) {
                          useBscDepositModalStore.getState().open();
                        } else {
                          openDepositModal("card");
                        }
                      }}
                      aria-label={t("deposit.addFunds")}
                      className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-[5px] bg-gray-900 dark:bg-white text-white dark:text-gray-900 active:scale-95 transition-transform"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                  {/* Balance tiles */}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="rounded-[5px] bg-black/[0.03] dark:bg-white/[0.04] px-3 py-2.5">
                      <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 mb-0.5">{t("menu.balance")}</p>
                      <p className="text-[15px] font-bold tabular-nums text-gray-900 dark:text-white">
                        {loadingBalance || balance === null ? '—' : `$${balance.toFixed(2)}`}
                      </p>
                    </div>
                    <div className="rounded-[5px] bg-black/[0.03] dark:bg-white/[0.04] px-3 py-2.5">
                      <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 mb-0.5">{t("menu.portfolio")}</p>
                      <p className="text-[15px] font-bold tabular-nums text-gray-900 dark:text-white">
                        {loadingBalance || portfolioValue === null ? '—' : `$${portfolioValue.toFixed(2)}`}
                      </p>
                    </div>
                  </div>
                  {/* View Profile — mobile-only shortcut. Desktop reaches
                      the profile via the UserPill in the sidebar; on
                      mobile the pill isn't visible so the user was
                      stranded with no way to open their own profile. */}
                  {ffUsername && (
                    <Link
                      href={`/profile/${ffUsername}`}
                      prefetch={false}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="w-full mt-2 flex items-center justify-center gap-2 h-10 rounded-[5px] bg-black/[0.04] dark:bg-white/[0.06] text-gray-900 dark:text-white text-[13px] font-semibold active:scale-[0.98] transition-transform"
                    >
                      <UserIcon size={16} />
                      {t("menu.viewProfile")}
                    </Link>
                  )}
                </div>
              )}

              {/* Source Selector + Language Selector — paired so the
                  user can switch platform and language from the same row */}
              <div className="mx-4 mb-3 flex items-center gap-2">
                <SourceSelector iconOnly={false} onSourceChange={() => setIsMobileMenuOpen(false)} />
                <LanguageSelector />
              </div>

              {/* Claim banner */}
              {redeemableCount > 0 && (
                <button
                  onClick={() => { openRedeemModal(); setIsMobileMenuOpen(false); }}
                  className="mx-4 mb-3 flex items-center gap-3 p-3 rounded-[5px] bg-emerald-500/[0.06] dark:bg-emerald-400/[0.06] border border-emerald-500/10"
                >
                  <Gift className="w-5 h-5 text-emerald-500 dark:text-emerald-400" />
                  <div className="flex-1 text-left">
                    <p className="text-[11px] text-gray-500 dark:text-gray-400">{t("menu.positionsToClaim")}</p>
                    <p className="text-[15px] font-bold text-emerald-600 dark:text-emerald-400">${redeemableValue.toFixed(0)}</p>
                  </div>
                </button>
              )}

              {/* Navigation — 2 por línea en mobile (caben de sobra y el menú
                  queda más compacto). El separador "More" hace col-span-2.
                  El ítem activo usa el pill sólido (igual que el sidebar). */}
              <div className="px-2 py-1 grid grid-cols-2 gap-1">
                {getNavigation(isLoggedIn, isHydrated, navSource).map((item) => {
                  // Mobile menu opens on tap (auth already resolved), so a
                  // data-driven skip is fine here — no refresh-CLS like the
                  // always-visible desktop rail. authOnly rows hidden when
                  // logged out. 2026-06-09.
                  if ((item as any).authOnly && !isLoggedIn) return null;
                  const Icon = item.icon;
                  const isExternal = (item as any).external;
                  const isAnimatedIcon = (item as any).isAnimatedIcon;
                  const isMarketsItem = item.name === "Markets";
                  const isActive = !isExternal && (
                    pathname === item.href ||
                    (item.href !== "/" && pathname?.startsWith(item.href)) ||
                    (isMarketsItem && (
                      pathname?.includes("/markets/") ||
                      pathname?.startsWith("/market/") ||
                      pathname?.startsWith("/polymarket/") ||
                      pathname?.startsWith("/kalshi/")
                    ))
                  );
                  const hasFeedbackNotification = item.name === "Feedback" && unreadFeedbackReplies > 0;
                  const hasAffiliateCount = item.name === "Affiliate" && activeReferrals > 0;
                  const hasCopytradingCount = item.name === "Copy" && copytradingCount > 0;
                  const isItemHovered = hoveredNavItem === item.name;

                  const itemClass = `flex items-center gap-3 px-3 py-2.5 rounded-[5px] text-[14px] font-medium transition-colors ${
                    isActive
                      ? "bg-gray-900 dark:bg-white text-white dark:text-gray-900 font-semibold"
                      : "text-gray-600 dark:text-gray-400 active:bg-black/[0.03] dark:active:bg-white/[0.04]"
                  }`;

                  const content = (
                    <>
                      <div className="relative">
                        {isAnimatedIcon ? (
                          <Icon size={20} isAnimated={!isActive} isHovered={isItemHovered && !isActive} className={hasFeedbackNotification ? "animate-pulse" : ""} />
                        ) : (
                          <Icon className={`w-5 h-5 ${hasFeedbackNotification ? "animate-pulse" : ""}`} {...({ strokeWidth: isActive ? 2.2 : 1.6 } as any)} />
                        )}
                        {hasFeedbackNotification && (
                          <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-[#FF3B30] rounded-[5px]" />
                        )}
                        {/* Copy / Affiliate counts as small overlays on the
                            icon corner (mirrors the desktop sidebar
                            pattern below — Pika 2026-05-24). The mobile
                            menu used to repeat the chip at the end of
                            the row; now consolidated to one place. */}
                        {hasCopytradingCount && (
                          <span className="absolute -top-1.5 -right-2 min-w-[16px] h-[16px] flex items-center justify-center px-1 text-[9px] font-bold rounded-[8px] bg-gray-900 dark:bg-white text-white dark:text-gray-900 border border-white dark:border-[#0a0a0b] leading-none">
                            {copytradingCount > 99 ? "99+" : copytradingCount}
                          </span>
                        )}
                        {hasAffiliateCount && (
                          <span className="absolute -top-1.5 -left-2 min-w-[16px] h-[16px] flex items-center justify-center px-1 text-[9px] font-bold rounded-[8px] bg-[#00FF9D] text-black border border-white dark:border-[#0a0a0b] leading-none">
                            {activeReferrals > 99 ? "99+" : activeReferrals}
                          </span>
                        )}
                      </div>
                      <span className="flex-1">{tNav(item.name)}</span>
                      {isExternal && <ExternalLink className="w-3.5 h-3.5 opacity-40" />}
                      {(item as any).badge && (
                        <span className="text-sm">{(item as any).badge}</span>
                      )}
                      {hasFeedbackNotification && (
                        <span className="min-w-[20px] h-5 flex items-center justify-center px-1.5 text-[10px] font-bold bg-[#FF3B30] text-white rounded-[5px]">
                          {unreadFeedbackReplies}
                        </span>
                      )}
                    </>
                  );

                  const linkHref = hasFeedbackNotification ? `${item.href}?tab=history` : item.href;
                  const authHideClass = item.name === "Home" ? " auth-hide" : "";

                  return (
                    <Fragment key={item.name}>
                      {/* "More" separator before the first secondary item. */}
                      {item.name === "Degenbox" && (
                        <div className="col-span-2 mx-1 mt-3 mb-1 flex items-center gap-2 select-none" aria-hidden>
                          <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500">{tNav("More")}</span>
                          <span className="flex-1 h-px bg-black/[0.06] dark:bg-white/[0.06]" />
                        </div>
                      )}
                      {isExternal ? (
                        <a href={item.href} target="_blank" rel="noopener noreferrer" onClick={() => setIsMobileMenuOpen(false)} className={itemClass + authHideClass}>
                          {content}
                        </a>
                      ) : (
                        <Link href={linkHref} prefetch={(item as any).prefetch ?? true} onClick={() => setIsMobileMenuOpen(false)} className={itemClass + authHideClass}>
                          {content}
                        </Link>
                      )}
                    </Fragment>
                  );
                })}
              </div>

              {/* Divider */}
              <div className="mx-6 my-2 h-px bg-black/[0.06] dark:bg-white/[0.06]" />

              {/* Bottom section: Settings + Theme + Socials */}
              <div className="px-4 pb-8 space-y-3">
                {/* Settings / Sign In — theme toggle sits on the far right of
                    this same row (moved out of the old bottom stats row, which
                    also carried the points/aura counters that we dropped). */}
                <div className="flex items-center gap-2">
                  {isLoggedIn ? (
                    <Link
                      href="/settings"
                      prefetch={false}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex-1 flex items-center gap-3 px-3 py-2.5 rounded-[5px] text-[14px] font-medium text-gray-600 dark:text-gray-400 active:bg-black/[0.03] dark:active:bg-white/[0.04]"
                    >
                      <Settings className="w-5 h-5" strokeWidth={1.6} />
                      <span>{t("nav.settings")}</span>
                    </Link>
                  ) : (
                    <button
                      onClick={() => { setIsMobileMenuOpen(false); login(); }}
                      className="flex-1 h-11 rounded-[5px] bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-[14px] font-semibold active:scale-[0.98] transition-transform"
                    >
                      {t("action.signIn")}
                    </button>
                  )}
                  <ThemeSwitcher />
                </div>

                {/* Telegram bot CTA — drawer twin of the desktop footer
                    pill. Closes the drawer first (so the user sees the
                    modal cleanly) and tags the deep-link source so we
                    can attribute conversions per surface. */}
                <button
                  type="button"
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    // Wait for the drawer slide-out to finish before
                    // popping the modal — otherwise it competes for
                    // attention with the closing animation.
                    setTimeout(() => setTgBotModalOpen(true), 200);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-[3px] border border-gray-200 dark:border-[#1A1A1A] active:bg-gray-100 dark:active:bg-[#1A1A1A] transition-colors"
                >
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-[3px] bg-[#229ED9]/15 text-[#229ED9] flex-shrink-0">
                    <TgGlyph className="w-3.5 h-3.5" />
                  </span>
                  <div className="flex flex-col items-start min-w-0 flex-1">
                    <span className="text-[14px] font-medium text-gray-900 dark:text-white leading-none">
                      {t("footer.tradeFromTelegram")}
                    </span>
                    <span className="font-mono text-[10px] text-[#229ED9] leading-none mt-1">
                      @FrenFlowBot
                    </span>
                  </div>
                </button>

                {/* Social links */}
                <div className="flex items-center gap-2 pt-1">
                  <a href="https://x.com/frenflow" target="_blank" rel="noopener noreferrer" aria-label="X" className="w-8 h-8 flex items-center justify-center rounded-[5px] bg-black/[0.04] dark:bg-white/[0.06]">
                    <svg className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
                  </a>
                  <a href="https://t.me/frenflow_announcements" target="_blank" rel="noopener noreferrer" aria-label="Telegram" className="w-8 h-8 flex items-center justify-center rounded-[5px] bg-black/[0.04] dark:bg-white/[0.06]">
                    <Send className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" strokeWidth={1.5} />
                  </a>
                  <a href="https://docs.frenflow.com/" target="_blank" rel="noopener noreferrer" aria-label="Docs" className="w-8 h-8 flex items-center justify-center rounded-[5px] bg-black/[0.04] dark:bg-white/[0.06]">
                    <BookOpen className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" strokeWidth={1.5} />
                  </a>
                  <Link href="/blog" prefetch={false} onClick={() => setIsMobileMenuOpen(false)} aria-label="Blog" className="w-8 h-8 flex items-center justify-center rounded-[5px] bg-black/[0.04] dark:bg-white/[0.06]">
                    <Newspaper className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" strokeWidth={1.5} />
                  </Link>
                  <a href="mailto:hello@frenflow.com" aria-label="Email" className="w-8 h-8 flex items-center justify-center rounded-[5px] bg-black/[0.04] dark:bg-white/[0.06]">
                    <Mail className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" strokeWidth={1.5} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

      {/* ==================== DESKTOP LEFT SIDEBAR ==================== */}
      <aside data-sidebar {...(isHydrated ? { 'data-hydrated': '' } : {})} className={`fixed left-0 top-0 bottom-0 bg-white dark:bg-[#18181D] border-r border-gray-200 dark:border-[#1A1A1A]/50 z-[250] hidden lg:block overflow-visible ${
        isHydrated ? "transition-[width] duration-300" : ""
      } ${isCollapsed ? "w-20" : "w-64"}`}>
        <div className="h-full flex flex-col overflow-visible">
          <div className={`flex-1 p-4 space-y-4 ${isCollapsed ? "overflow-visible" : "overflow-y-auto"}`}>
            {/* Logo and Toggle Button */}
            <div className="flex items-center justify-between mb-6">
              <Link href={isLoggedIn ? getMarketsHref(isLoggedIn, isHydrated) : "/"} prefetch={false} className={`flex items-center gap-2 group ${isCollapsed ? "mx-auto" : "ml-3"}`}>
                <CdnImage
                  src="/brand/frenflow_logo_light.png"
                  alt="FrenFlow"
                  width={24}
                  height={28}
                  className="w-6 h-7 flex-shrink-0 object-contain dark:hidden"
                />
                <CdnImage
                  src="/brand/frenflow_logo.png"
                  alt="FrenFlow"
                  width={24}
                  height={28}
                  className="w-6 h-7 flex-shrink-0 object-contain hidden dark:block"
                />
                {!isCollapsed && (
                  <div className="flex items-baseline gap-0 overflow-hidden">
                    <span className="text-xl font-monument font-bold tracking-tight text-gray-900 dark:text-white group-hover:text-accent-primary transition-colors whitespace-nowrap">
                      FREN
                    </span>
                    <span className="text-xl font-satoshi font-light tracking-normal text-gray-900 dark:text-white group-hover:text-accent-primary transition-colors whitespace-nowrap">
                      FLOW
                    </span>
                  </div>
                )}
              </Link>
              {!isCollapsed && (
                <button
                  onClick={() => setIsCollapsed(true)}
                  className="p-1.5 rounded-[5px] hover:bg-gray-100 dark:hover:bg-[#0a0a0b] transition-colors"
                  aria-label="Collapse sidebar"
                >
                  <ChevronLeftIcon size={16} className="text-gray-600 dark:text-gray-300" />
                </button>
              )}
            </div>

            {isCollapsed && (
              <button
                onClick={() => setIsCollapsed(false)}
                className="w-full p-2 rounded-[5px] hover:bg-gray-100 dark:hover:bg-[#0a0a0b] transition-colors flex items-center justify-center mb-4"
                aria-label="Expand sidebar"
              >
                <ChevronRightIcon size={16} className="text-gray-600 dark:text-gray-300" />
              </button>
            )}

            {/* Navigation Menu */}
            <div className={`bg-gray-100/50 dark:bg-[#0a0a0b] rounded-[5px] border border-gray-200/50 dark:border-[#1A1A1A]/50 ${
              isCollapsed ? "p-1.5" : "p-3"
            }`}>
              {/* 2026-05-22 (Pika): tightened sidebar nav. Was `space-y-1`
                  (4px) which left ~14px of total gap per row given the
                  default Link padding. `space-y-0` collapses the row
                  margins; per-row padding stays the same so click
                  targets are unchanged. Outer container also dropped
                  from p-4 / p-2 → p-3 / p-1.5 to reduce the dead
                  whitespace around the nav block. */}
              <nav className="space-y-0">
                {getNavigation(isLoggedIn, isHydrated, navSource).map((item, index) => {
                  const Icon = item.icon;
                  const isExternal = (item as any).external;
                  const isAnimatedIcon = (item as any).isAnimatedIcon;
                  const isMarketsItem = item.name === "Markets";
                  const isActive = !isExternal && (
                    pathname === item.href ||
                    (item.href !== "/" && pathname?.startsWith(item.href)) ||
                    (isMarketsItem && (
                      pathname?.includes("/markets/") ||
                      pathname?.startsWith("/market/") ||
                      pathname?.startsWith("/polymarket/") ||
                      pathname?.startsWith("/kalshi/")
                    ))
                  );
                  const shortcutKey = index + 1; // 1-8

                  const linkClassName = isCollapsed
                    ? `group/link relative flex items-center justify-center p-2.5 rounded-[5px] font-satoshi font-bold text-sm transition-[background-color,color] duration-200 ${
                        isActive
                          ? "bg-gray-900 dark:bg-white text-white dark:text-gray-900"
                          : "text-gray-600 dark:text-[#A1A1AA] hover:bg-gray-100 dark:hover:bg-[#18181D]"
                      }`
                    : `group/link relative flex items-center gap-3 px-3 py-2.5 rounded-[5px] font-satoshi font-bold text-sm transition-[background-color,color] duration-200 border-2 overflow-hidden ${
                        isActive
                          ? "bg-gray-900 dark:bg-white text-white dark:text-gray-900 border-gray-900 dark:border-white"
                          : "text-gray-600 dark:text-[#A1A1AA] border-transparent hover:text-gray-900 dark:hover:text-white"
                      }`;

                  // Check if this is Feedback with unread replies
                  const hasFeedbackNotification = item.name === "Feedback" && unreadFeedbackReplies > 0;
                  const hasAffiliateCount = item.name === "Affiliate" && activeReferrals > 0;
                  const hasCopytradingCount = item.name === "Copy" && copytradingCount > 0;

                  const isItemHovered = hoveredNavItem === item.name;

                  const linkContent = (
                    <>
                      {/* Slide effect background - only for non-active, expanded items */}
                      {!isActive && !isCollapsed && (
                        <span className="absolute inset-0 bg-white/90 dark:bg-white/[0.08] -translate-x-full group-hover/link:translate-x-0 transition-transform duration-300 ease-out" />
                      )}
                      <div className="relative z-10">
                        {isAnimatedIcon ? (
                          <Icon size={16} isAnimated={!isActive} isHovered={isItemHovered && !isActive} className={`flex-shrink-0 ${hasFeedbackNotification ? "animate-pulse" : ""}`} />
                        ) : (
                          <Icon className={`w-4 h-4 flex-shrink-0 ${hasFeedbackNotification ? "animate-pulse" : ""}`} />
                        )}
                        {hasFeedbackNotification && (
                          <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-[#FF4444] rounded-full animate-ping" />
                        )}
                        {hasFeedbackNotification && (
                          <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-[#FF4444] rounded-[5px]" />
                        )}
                        {/* Copy follows badge on the icon (Pika 2026-05-24).
                            Previously rendered as a row-level chip between
                            the label and ⌘-shortcut — broke the visual
                            rhythm of every other row. App-icon pattern keeps
                            it small and unobtrusive in both expanded and
                            collapsed states. 99+ cap so the bubble never
                            outgrows the icon. */}
                        {hasCopytradingCount && (
                          <span className="absolute -top-1.5 -right-2 min-w-[16px] h-[16px] flex items-center justify-center px-1 text-[9px] font-bold rounded-[8px] bg-gray-900 dark:bg-white text-white dark:text-gray-900 border border-white dark:border-[#0a0a0b] leading-none">
                            {copytradingCount > 99 ? "99+" : copytradingCount}
                          </span>
                        )}
                        {hasAffiliateCount && (
                          <span className="absolute -top-1.5 -left-2 min-w-[16px] h-[16px] flex items-center justify-center px-1 text-[9px] font-bold rounded-[8px] bg-[#00FF9D] text-black border border-white dark:border-[#0a0a0b] leading-none">
                            {activeReferrals > 99 ? "99+" : activeReferrals}
                          </span>
                        )}
                      </div>
                      {!isCollapsed && (
                        <>
                          <span className="relative z-10 flex-1">{tNav(item.name)}</span>
                          {isExternal && <ExternalLink className="relative z-10 w-3 h-3 opacity-50" />}
                          {(item as any).badge && (
                            <span className="relative z-10 text-xs animate-pulse">{(item as any).badge}</span>
                          )}
                          {hasFeedbackNotification && (
                            <span className="relative z-10 px-1.5 py-0.5 text-[10px] font-bold bg-[#FF4444] text-white rounded-[5px] animate-pulse">
                              {unreadFeedbackReplies}
                            </span>
                          )}
                          {/* Row-level Copy / Affiliate badges removed
                              2026-05-24 — both now render as small icon-
                              overlays inside the icon wrapper above
                              (Pika feedback). Keep Feedback's chip for
                              now because the red colour + pulse is
                              part of the urgency signal there. */}
                          <kbd className={`relative z-10 ml-auto px-1.5 py-0.5 text-[10px] font-mono rounded transition-colors ${
                            isActive
                              ? "bg-white/20 text-white dark:bg-gray-900/20 dark:text-gray-900"
                              : "bg-gray-200/50 dark:bg-[#1A1A1A]/50 text-gray-400 dark:text-[#71717A] group-hover/navitem:text-accent-primary"
                          }`}>
                            ⌘{shortcutKey}
                          </kbd>
                        </>
                      )}
                    </>
                  );

                  // Use history tab when there are unread feedback replies
                  const feedbackLinkHref = hasFeedbackNotification ? `${item.href}?tab=history` : item.href;

                  return (
                    <Fragment key={item.name}>
                      {/* "More" separator — injected before the first secondary
                          item (Degenbox). Expanded: label + line. Collapsed:
                          just a line. 2026-06-09 nav reorg. */}
                      {item.name === "Degenbox" && (
                        isCollapsed ? (
                          <div className="my-2 mx-2.5 h-px bg-gray-200 dark:bg-[#1A1A1A]" aria-hidden />
                        ) : (
                          <div className="mt-3 mb-1 px-3 flex items-center gap-2 select-none" aria-hidden>
                            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500">{tNav("More")}</span>
                            <span className="flex-1 h-px bg-gray-200 dark:bg-[#1A1A1A]" />
                          </div>
                        )
                      )}
                      <div className={`relative group/navitem${item.name === "Home" ? " auth-hide" : ""}${(item as any).authOnly ? " nav-auth-only" : ""}`}>
                      {isExternal ? (
                        <a
                          href={item.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={linkClassName}
                        >
                          {linkContent}
                        </a>
                      ) : (
                        <Link
                          href={feedbackLinkHref}
                          prefetch={(item as any).prefetch ?? true}
                          className={linkClassName}
                        >
                          {linkContent}
                        </Link>
                      )}

                      {/* Tooltip for collapsed state */}
                      {isCollapsed && (
                        <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 px-3 py-1.5 bg-white dark:bg-[#18181D] text-gray-900 dark:text-white text-sm font-satoshi font-semibold rounded-[5px] shadow-lg ring-1 ring-black/[0.08] dark:ring-white/[0.1] opacity-0 invisible group-hover/navitem:opacity-100 group-hover/navitem:visible transition-opacity duration-200 whitespace-nowrap z-[300] pointer-events-none">
                          {item.name} <span className="opacity-50 font-mono text-xs ml-1">⌘{shortcutKey}</span>
                          <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-white dark:border-r-[#18181D]" />
                        </div>
                      )}
                      </div>
                    </Fragment>
                  );
                })}
              </nav>
            </div>

            {/* Profile Widget — removido del sidebar (avatar + nombre + Add Funds).
                La identidad y el "Fund your wallet" viven en el header. */}
            {/* <ProfileWidget isCollapsed={isCollapsed} /> */}

            {/* Streak Widget - Hidden for now */}
            {/* <StreakWidget isCollapsed={isCollapsed} /> */}
          </div>

          {/* Social icons moved to the bottom footer bar (LiveTicker.tsx) so
              the sidebar keeps a clean nav-only surface. A bottom padding
              compensates for the fixed 44px footer so the last sidebar
              widget (ProfileWidget) isn't hidden behind it. */}
          {showTicker && <div className="h-11 flex-shrink-0" aria-hidden />}
        </div>
      </aside>

      {/* ==================== MOBILE BOTTOM NAVIGATION — iOS Glass ==================== */}
      {/* Fondo OPACO (sin backdrop-blur) — ver nota en el header móvil: el blur
          en una barra fixed repinta cada frame de scroll y cuelga el móvil. */}
      <nav className={`lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white/95 dark:bg-[#18181D]/95 border-t border-white/30 dark:border-white/[0.08] shadow-[0_-1px_20px_rgba(0,0,0,0.06)] dark:shadow-[0_-1px_20px_rgba(0,0,0,0.4)] safe-area-pb transition-transform duration-300 rounded-t-[5px] ${isBubbleflow ? 'translate-y-full' : showMobileNav ? 'translate-y-0' : 'translate-y-full'}`}>
        <div className="grid grid-cols-5 h-[52px]">
          {[
            // 5-slot order after sidebar reorder 2026-05-18:
            // Markets | Traders | Post (center) | Copy | Activity.
            // Slice avoids manually re-listing items (duplicate-key
            // bug if the source array order shifts).
            ...getMobileNavigation(isLoggedIn, isHydrated).slice(0, 2),
            { name: "Post", href: "__create__", icon: Plus },
            ...getMobileNavigation(isLoggedIn, isHydrated).slice(2, 4),
          ].map((item) => {
            const Icon = item.icon;
            const isCreate = item.href === "__create__";
            const isActive = !isCreate && (pathname === item.href || pathname?.startsWith(item.href));
            const showBadge = item.name === "Activity" && newActivityCount > 0 && !isActive;

            const content = (
              <div className="flex flex-col items-center justify-center gap-[3px] h-full">
                <div className="relative">
                  <Icon
                    className={`w-[21px] h-[21px] ${
                      isActive
                        ? "text-gray-900 dark:text-white"
                        : "text-gray-400 dark:text-gray-500"
                    }`}
                    strokeWidth={isActive ? 2.2 : 1.8}
                  />
                  {showBadge && (
                    <span className="absolute -top-1 -right-2 flex items-center justify-center min-w-[15px] h-[15px] px-0.5 text-[8px] font-bold bg-[#FF3B30] text-white rounded-[5px]">
                      {newActivityCount > 9 ? "9+" : newActivityCount}
                    </span>
                  )}
                </div>
                <span className={`text-[10px] leading-none ${
                  isActive
                    ? "font-semibold text-gray-900 dark:text-white"
                    : "font-medium text-gray-400 dark:text-gray-500"
                }`}>
                  {tNav(item.name)}
                </span>
              </div>
            );

            if (isCreate) {
              return (
                <button key="create" onClick={handleCreatePostClick} className="h-full">
                  {content}
                </button>
              );
            }
            return (
              <Link
                key={item.name}
                href={item.href}
                className="h-full"
              >
                {content}
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Create Post Modal */}
      <CreatePostModal
        isOpen={isCreatePostOpen}
        onClose={() => setIsCreatePostOpen(false)}
        initialMarketPreview={currentMarketPreview}
      />

      {/* Global Redeem Modal */}
      <RedeemModal />

      {/* Deposit Modal (Bridge + Tip) */}
      <DepositModal walletAddress={walletAddress || ""} />
      <BscDepositModalWrapper />

      {/* Mandatory onboarding for new users (Enable-Trading + deposit
          wallet creation). Mounted outside <Deferred> so it can race
          against FrenPicker / Privy modals on fresh signup. */}
      <OnboardingProvider />

      {/* Global Trading Modals (shared across all cards) */}
      <SharedTradingModals />

      {/* Floating support chat (gated to authenticated users internally) */}
      <SupportChatWidget />

      {/* Global level-up celebration — fires on ALL viewports (mobile + desktop)
          and replays on re-entry if a level-up was missed. See LevelUpWatcher. */}
      <LevelUpWatcher />
      <HyperliquidEnableModalGlobal />
      <HyperliquidWithdrawModalGlobal />
      <PolymarketWithdrawModalGlobal />
      <PolymarketVolumeMilestoneModalGlobal />

      {/* Telegram bot info modal — opened from the mobile drawer item
          above. Source tag distinguishes conversions originating in the
          drawer vs. desktop footer. */}
      <TelegramBotModal
        open={tgBotModalOpen}
        onClose={() => setTgBotModalOpen(false)}
        source="web_mobile_drawer"
      />

      {/* ==================== MAIN CONTENT AREA ==================== */}
      <div data-main-content {...(isHydrated ? { 'data-hydrated': '' } : {})} className={`${isHydrated ? "transition-[margin] duration-300" : ""} ${isHydrated ? (isCollapsed ? "lg:ml-20" : "lg:ml-64") : ""}`}>
        {/* Desktop Header */}
        <header data-desktop-header {...(isHydrated ? { 'data-hydrated': '' } : {})} className={`hidden lg:block fixed top-0 right-0 z-[100] border-b border-gray-200/30 dark:border-gray-800/30 backdrop-blur-2xl bg-white/70 dark:bg-[#0f0f12]/80 shadow-lg ${isHydrated ? "transition-[left] duration-300" : ""}`} style={isHydrated ? { left: isCollapsed ? '5rem' : '16rem' } : undefined}>
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-14 gap-4">
              {/* Page Title - Special handling for Terminal */}
              {isTerminalPage && terminalMarket ? (
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {/* Market Image */}
                  {terminalMarket.image && (
                    <div className="w-8 h-8 rounded-[5px] overflow-hidden bg-[#18181D] flex-shrink-0">
                      <CdnImage
                        src={terminalMarket.image}
                        alt={terminalMarket.question}
                        width={32}
                        height={32}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}

                  {/* Market Name */}
                  <div className="flex-1 min-w-0">
                    <h1 className="text-sm md:text-base font-satoshi font-bold text-gray-900 dark:text-white truncate max-w-[200px] md:max-w-[400px]">
                      {terminalMarket.question}
                    </h1>
                    <div className="flex items-center gap-2 text-[10px] text-[#A1A1AA]">
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${terminalMarket.isActive ? "bg-[#00FF9D]/10 text-[#00FF9D]" : "bg-gray-500/10 text-gray-500"}`}>
                        {terminalMarket.isActive ? "LIVE" : "CLOSED"}
                      </span>
                      <span className="text-gray-400">Vol: {formatVolume(terminalMarket.volume)}</span>
                      <span className="text-gray-400">Liq: {formatVolume(terminalMarket.liquidity)}</span>
                    </div>
                  </div>

                  {/* Terminal Prices */}
                  <div className="hidden md:flex items-center gap-2">
                    <div className="flex items-center gap-1.5 px-2.5 py-1 bg-[#00FF9D]/10 rounded-[5px]">
                      <span className="text-[10px] text-[#A1A1AA] uppercase">Yes</span>
                      <span className="text-sm font-inter-tight font-bold text-[#00FF9D]">
                        {(terminalMarket.yesPrice * 100).toFixed(1)}¢
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 px-2.5 py-1 bg-[#FF1744]/10 rounded-[5px]">
                      <span className="text-[10px] text-[#A1A1AA] uppercase">No</span>
                      <span className="text-sm font-inter-tight font-bold text-[#FF1744]">
                        {(terminalMarket.noPrice * 100).toFixed(1)}¢
                      </span>
                    </div>
                  </div>

                  {/* Link to Polymarket */}
                  <a
                    href={`https://polymarket.com/market/${terminalMarket.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-[#A1A1AA] hover:text-accent-primary hover:bg-[#18181D] rounded-[5px] transition-colors"
                    title={t("action.viewOnPolymarket")}
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3 min-w-[290px]">
                    {/* SEO: on the homepage this slot used to render an EMPTY
                        <h1> (flagged by audits — empty H1 + duplicate with the
                        hero's real H1). Skip it on home; the hero owns the H1. */}
                    {!(pathname === "/" || pathname === "/home-v2") && (
                    <h1 className="text-lg md:text-xl font-monument font-bold text-gray-900 dark:text-white whitespace-nowrap">
                      {(pathname === "/markets" || pathname === "/polymarket/markets" || pathname === "/kalshi/markets" || pathname === "/predictfun/markets") && t("page.markets")}
                      {(pathname?.startsWith("/markets/") || pathname?.startsWith("/polymarket/markets/") || pathname?.startsWith("/kalshi/markets/") || pathname?.startsWith("/predictfun/markets/")) && (() => {
                        const segments = pathname.split("/");
                        const category = pathname?.startsWith("/polymarket/") || pathname?.startsWith("/kalshi/") || pathname?.startsWith("/predictfun/") ? segments[3] : segments[2];
                        if (!category) return t("page.markets");
                        // tCategory handles the slug → translated label
                        // lookup (e.g. `politics` → "Política"), and falls
                        // back to a Title-Case render of the slug for
                        // anything we haven't catalogued yet.
                        return tCategory(category) || category.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                      })()}
                      {pathname === "/predictor" && "Predictor"}
                      {pathname === "/degenbox" && "Degenbox"}
                      {pathname?.startsWith("/bubbleflow") && "BubbleFlow"}
                      {pathname === "/activity" && t("page.activity")}
                      {pathname === "/traders" && t("page.traders")}
                      {pathname === "/rewards" && "Rewards"}
                      {pathname === "/copytrading" && t("page.copy")}
                      {(pathname === "/affiliate" || pathname === "/affiliate/toolkit") && t("page.affiliate")}
                      {pathname === "/feedback" && t("page.feedback")}
                      {pathname === "/portfolio" && t("page.portfolio")}
                      {(pathname?.startsWith("/event/") || pathname?.startsWith("/polymarket/event/") || pathname?.startsWith("/kalshi/event/") || pathname?.startsWith("/predictfun/event/")) && t("page.event")}
                      {(pathname?.startsWith("/market/") || pathname?.startsWith("/polymarket/market/") || pathname?.startsWith("/kalshi/market/") || pathname?.startsWith("/predictfun/market/")) && t("page.market")}
                      {pathname?.startsWith("/profile/") && t("page.profile")}
                      {pathname?.startsWith("/traders/") && t("page.trader")}
                      {pathname?.startsWith("/post/") && "Post"}
                      {pathname?.startsWith("/terminal/") && "Terminal"}
                      {pathname?.startsWith("/elon") && "Elon Tracker"}
                      {pathname === "/og-pass" && "OG Pass"}
                      {pathname === "/settings" && t("page.settings")}
                      {pathname === "/blog" && "Prediction Markets Blog"}
                      {pathname?.startsWith("/blog/") && "Prediction Markets Blog"}
                      {pathname?.startsWith("/geopolitics") && "Geopolitics"}
                      {pathname?.startsWith("/uma") && "UMA"}
                      {pathname?.startsWith("/apuestas-la-velada-6") && "La Velada del Año 6"}
                      {pathname?.startsWith("/prediction-markets-open-interest") && "Open Interest"}
                      {pathname === "/frenlinks" && "FrenLinks"}
                      {pathname?.startsWith("/claim/") && "Claim"}
                    </h1>
                    )}
                    {pathname === "/frenlinks" && (
                      <div className="flex items-center gap-1.5 ml-2">
                        <div className="w-7 h-7 rounded-[5px] bg-[#0D2E4E] flex items-center justify-center">
                          <img src="/brand/frenflow_logo.png" alt="FrenFlow" className="w-4 h-4 object-contain" />
                        </div>
                        <div className="w-7 h-7 rounded-[5px] bg-[#0D2E4E] flex items-center justify-center -ml-1">
                          <img src="/polymarket-logo-blue.png" alt="Polymarket" className="w-4 h-4 object-contain" />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Search Bar - Only show when NOT in terminal */}
                  <div className="flex-1 max-w-xl">
                    <GlobalSearch />
                  </div>
                </>
              )}

              {/* Right Actions — fixed min-width prevents search/selector from shifting */}
              <div className="flex items-center gap-1.5 flex-shrink-0 min-w-[340px] justify-end">
                {/* Claim — desktop, aligned with balance pills */}
                {redeemableCount > 0 && (
                  <div className="auth-only flex items-center gap-0.5 backdrop-blur-xl bg-amber-50 dark:bg-amber-400/10 border border-amber-300/60 dark:border-amber-300/20 rounded-[5px] px-1 py-0.5">
                    <button
                      onClick={openRedeemModal}
                      className="flex items-center gap-1.5 px-2 py-1 rounded-[5px] hover:bg-amber-100/70 dark:hover:bg-amber-400/20 transition-colors"
                      title={`${redeemableCount} position${redeemableCount > 1 ? 's' : ''} to claim`}
                      aria-label={`Claim ${redeemableCount} position${redeemableCount > 1 ? 's' : ''}`}
                    >
                      <Gift className="w-3.5 h-3.5 text-amber-700 dark:text-amber-300" aria-hidden="true" />
                      <span className="text-xs font-semibold text-amber-700 dark:text-amber-300">Claim</span>
                      <span className="text-xs font-semibold text-amber-700 dark:text-amber-300 tabular-nums">
                        ${redeemableValue.toFixed(2)}
                      </span>
                    </button>
                  </div>
                )}

                {/* Wallet Display — Source-aware (PM/Kalshi) */}
                <SourceAwareBalance />

                {/* Auth-only buttons - Notifications */}
                <NotificationsDropdown
                  unreadCount={unreadNotifications}
                  onCountChange={setUnreadNotifications}
                />

                <Link
                  href="/settings"
                  prefetch={false}
                  aria-label="Account settings"
                  className="auth-only p-2 rounded-[5px] hover:bg-gray-100 dark:hover:bg-[#18181D] transition-colors"
                >
                  <SettingsIcon size={20} className="text-gray-600 dark:text-gray-300" />
                </Link>

                {/* Theme switcher sits right next to the Source selector so
                    "display settings" (dark/light + venue) group together. */}
                <ThemeSwitcher />

                {/* Source Selector (Polymarket/Kalshi) — fixed width, before user pill */}
                <SourceSelector />

                {/* Language selector — sits right of the source selector so
                    "what venue / what language" group together. Default
                    EN; ES added for the Polymarket-only translation pass. */}
                <LanguageSelector />

                {process.env.NEXT_PUBLIC_MOCK_AUTH === "true" ? (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-[#CCFF00]/20 rounded-[5px]">
                    <div className="w-2 h-2 bg-[#CCFF00] rounded-[5px]"></div>
                    <span className="text-xs font-medium text-[#CCFF00]">Mock User</span>
                  </div>
                ) : !privyReady ? (
                  // Privy still loading. The skeleton must match the final
                  // UserPill's footprint (~160px wide, 36px tall) — otherwise
                  // the whole `min-w-[340px]` row reflows horizontally when
                  // Privy resolves, and every table cell below picks up the
                  // shift in the CLS report. Optimistic-logged-in users see
                  // a pill-shaped placeholder; fresh users see AuthButton
                  // directly (no placeholder swap on their path).
                  isLoggedIn ? (
                    <div className="flex items-center gap-2 px-2 py-1 bg-gray-200 dark:bg-[#18181D] rounded-[5px] w-[160px] h-9">
                      <div className="w-7 h-7 bg-gray-300 dark:bg-[#0a0a0b] rounded-[5px] flex-shrink-0" />
                      <div className="flex-1 h-3 bg-gray-300 dark:bg-[#0a0a0b] rounded" />
                    </div>
                  ) : (
                    <AuthButton />
                  )
                ) : !isLoggedIn ? (
                  <AuthButton />
                ) : (
                  <SafeUserPill fallback={<AuthButton />} />
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Main Content with Right Sidebar */}
        <div className={`w-full max-w-none overflow-x-clip ${isBubbleflow ? "pt-12 md:pt-16 pb-0" : isTerminalPage ? "pt-12 lg:pt-14 pb-20 lg:pb-0" : isOGPassPage ? "pt-12 lg:pt-10 pb-0" : "pt-12 lg:pt-16 pb-[calc(3.5rem+env(safe-area-inset-bottom))] lg:pb-8"}`}>
          <CopyPausedBanner />
          <EoaSweepBanner />
          <DepositWrapBanner />
          <CopytradingSkipBanner />
          <PolymarketGeoblockBanner />
          <DextoolsWelcomeBanner />
          {!showRightSidebar ? (
            // Default: no right sidebar — layout owns horizontal padding.
            // `min-h-[calc(100dvh-8rem)]` (dvh, not vh) so iOS Safari doesn't
            // reserve space for the collapsed address-bar chrome. With the
            // old `100vh`, short trader/event pages ended with ~200-400px
            // of scrollable dark dead space between the mobile bottom nav
            // and the browser chrome.
            <div className="w-full transition-none">
              <div className={isBubbleflow ? "h-[calc(100dvh-3rem)] md:h-[calc(100dvh-4rem)] lg:h-[calc(100dvh-4rem)]" : isTerminalPage ? "h-[calc(100dvh-3.5rem)] lg:h-[calc(100dvh-3.5rem)]" : (isHyperliquidLanding || isHomePage) ? "min-h-[calc(100dvh-8rem)]" : "min-h-[calc(100dvh-8rem)] px-4 lg:px-6"}>
                {children}
              </div>
            </div>
          ) : (
            // Other pages with right sidebar (Activity, Blog, etc.)
            <div className="lg:h-[calc(100dvh-3.5rem)] lg:overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 px-2 lg:px-6 h-full">
                {/* Main Content Area - Scrollable */}
                <div className="lg:col-span-9 lg:overflow-y-auto lg:scrollbar-hide lg:pb-8 lg:pr-4">
                  <div className="min-h-[calc(100dvh-8rem)] lg:min-h-0">
                    {children}
                  </div>
                </div>

                {/* Right Sidebar - Fixed/Sticky */}
                <div className="hidden lg:block lg:col-span-3 lg:overflow-y-auto lg:scrollbar-hide lg:pb-8">
                  <div className="space-y-4 pt-2">
                    {/* Share-to-Earn promo only on /activity — that's the
                        highest-intent surface for converting consumers into
                        sharers, and the user explicitly scoped it there. */}
                    {pathname === '/activity' && <ShareToEarnPromoCard />}
                    <TrendingMarkets />
                    {/* TrendingPits hidden alongside the sidebar nav
                        entry — re-enable when pits ships. */}
                    {/* <TrendingPits /> */}
                    <WhoToFollow />
                    <MostCopied />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// `useCurrentSource` reads `useSearchParams`, which Next 16 refuses to
// prerender unless the consumer is wrapped in <Suspense>. Isolating the
// call here keeps every other page statically renderable while still
// letting the mobile menu show the source-aware deposit address.
